import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useState } from 'react';

import Button from '../../../library/inputs/Button';
import Flash from '../../../library/utils/Flash';
import HiringMeetingTemplateListTable from './HiringMeetingTemplateListTable';
import HiringMeetingTemplateSelectInput from '../../../library/inputs/HiringMeetingTemplateSelectInput';
import LoadingSpinner from '../../../library/utils/LoadingSpinner';
import Section from '../../../library/layout/Section';
import SelectBaseTemplateModal from '../../../library/data-display/SelectBaseTemplateModal';
import TextInput from '../../../library/inputs/TextInput';
import useSyncStateWithQuery from '../../../../hooks/use-sync-state-with-query';
import { StyledContainer, StyledFiltersContainer, StyledHeaderActions } from './styles';
import { useHiringMeetingTemplates } from '../../../../hooks/queries/hiring-meeting-templates';

import type { ChangeEvent } from 'react';
import { correctPath } from 'libraries/gem';

const HiringMeetingTemplateList = () => {
  const [search, querySearch, setSearch] = useSyncStateWithQuery<string>('q', '', { delay: 200 });
  const [showArchived, , setShowArchived] = useSyncStateWithQuery<string>('show_archived', 'false');
  const [pageNumber, queryPageNumber, setPageNumber] = useSyncStateWithQuery<string>('page', '1');

  const [isSelectBaseTemplateModalOpen, setIsSelectBaseTemplateModalOpen] = useState<boolean>(false);

  const {
    data: hiringMeetingTemplates,
    error,
    isFetching,
  } = useHiringMeetingTemplates({
    archived: showArchived === 'true',
    name: querySearch,
    limit: 10,
    offset: 10 * (parseInt(queryPageNumber, 10) - 1),
  });

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (parseInt(pageNumber, 10) > 1) {
      setPageNumber('1', { method: 'replace' });
    }
  }, [pageNumber]);

  const handleShowArchivedChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setShowArchived(JSON.stringify(e.target.checked));
  }, []);

  const handlePageNumberChange = useCallback((value: number) => {
    setPageNumber(value.toString());
  }, []);

  return (
    <StyledContainer>
      <Helmet>
        <title>Hiring Meeting Templates | Gem Scheduling</title>
      </Helmet>
      <Section
        headerActions={
          <StyledHeaderActions>
            <Button
              color="gem-outline"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setIsSelectBaseTemplateModalOpen(true)}
              value="New template from base"
            />
            <Link to={correctPath('/app/hiring-meeting-templates/create')}>
              <Button
                color="gem-blue"
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
                value="New template"
              />
            </Link>
          </StyledHeaderActions>
        }
        title="Hiring meeting templates"
      >
        <SelectBaseTemplateModal
          isOpen={isSelectBaseTemplateModalOpen}
          onToggle={() => setIsSelectBaseTemplateModalOpen(!isSelectBaseTemplateModalOpen)}
          redirectPath={correctPath('/app/hiring-meeting-templates/create')}
          resourceName="hiring meeting template"
          selectInputComponent={HiringMeetingTemplateSelectInput}
        />
        <StyledFiltersContainer>
          <TextInput
            isAutofocus
            label="Search"
            leftIcon={isFetching ? <LoadingSpinner /> : <FontAwesomeIcon icon={faSearch} />}
            onChange={handleSearchChange}
            placeholder="Hiring meeting template name"
            value={search}
          />
        </StyledFiltersContainer>
        <Flash
          message={error?.message}
          showFlash={Boolean(error)}
          type="danger"
        />
        {hiringMeetingTemplates && (
          <HiringMeetingTemplateListTable
            hiringMeetingTemplates={hiringMeetingTemplates.hiring_meeting_templates}
            onPageNumberChange={handlePageNumberChange}
            onShowArchivedChange={handleShowArchivedChange}
            pageNumber={parseInt(pageNumber, 10)}
            showArchived={showArchived === 'true'}
            totalCount={hiringMeetingTemplates.total}
          />
        )}
      </Section>
    </StyledContainer>
  );
};

export default HiringMeetingTemplateList;
